// VX-4302: the cross-tab presence + vote registry for the escape hatch. Session state only —
// BroadcastChannel-synced, never durably persisted — keyed by a per-tab id (see TAB_ID in
// escapeHatchCoordination). Each entry carries a `lastSeen` heartbeat so a tab that closed without
// cleanup ages out; the TTL must be shorter than the ladder-exhaustion time so a dead tab can
// neither veto a legitimate bump (an unsatisfiable quorum) nor rehydrate as a phantom exhausted
// vote. Pure functions so the membership/TTL/counting logic is testable in isolation.

export interface ObserverEntry {
  // Per-tab identity (see TAB_ID in coordination): unique per browsing context, stable across a
  // reload. NOT the `/viewer/{id}` slot — two tabs on the same slot are two distinct observers.
  tabId: string;
  // Generation this observer is connected to / voting against.
  generation: number;
  // Eligibility: requested frames within the observation window. Idle/prefetch-only windows are
  // false and never count toward quorum's denominator.
  hasDemand: boolean;
  // Standing "ladder-exhausted for this generation" vote.
  exhausted: boolean;
  // Epoch ms of this observer's last heartbeat.
  lastSeen: number;
}

export type ObserverRegistry = Record<string, ObserverEntry>;

// Shorter than ladder-exhaustion time (~3–5 min): ~2 recreate cycles. Tunable.
export const OBSERVER_TTL_MS = 150_000;

const isFresh = (e: ObserverEntry, now: number, ttlMs: number): boolean =>
  now - e.lastSeen <= ttlMs;

export function upsertObserver(registry: ObserverRegistry, entry: ObserverEntry): ObserverRegistry {
  return { ...registry, [entry.tabId]: entry };
}

export function pruneStale(
  registry: ObserverRegistry,
  now: number,
  ttlMs: number
): ObserverRegistry {
  const next: ObserverRegistry = {};
  for (const entry of Object.values(registry)) {
    if (isFresh(entry, now, ttlMs)) next[entry.tabId] = entry;
  }
  return next;
}

// Record this observer and drop any aged past the TTL in one step — every write site upserts and
// prunes together, so this keeps the two from drifting apart.
export function updateRegistry(
  registry: ObserverRegistry,
  entry: ObserverEntry,
  now: number,
  ttlMs: number = OBSERVER_TTL_MS
): ObserverRegistry {
  return pruneStale(upsertObserver(registry, entry), now, ttlMs);
}

export function countEligible(registry: ObserverRegistry, now: number, ttlMs: number): number {
  return Object.values(registry).filter((e) => e.hasDemand && isFresh(e, now, ttlMs)).length;
}

export function countExhaustedVotes(
  registry: ObserverRegistry,
  generation: number,
  now: number,
  ttlMs: number
): number {
  return Object.values(registry).filter(
    (e) => e.hasDemand && e.exhausted && e.generation === generation && isFresh(e, now, ttlMs)
  ).length;
}
